import { selectAppUser, selectUser } from '@/lib/redux/app.selectors';
import { useGetRootAccountInfo } from '@/shared/hooks/useGetRootAccountInfo';
import Intercom from '@intercom/messenger-js-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import packageJson from '../../../package.json';

const APP_ID = 'p9968ft4';
const PLATFORM_VERSION = `adminUI@v${packageJson.version}`;

export const useIntercom = () => {
  const appUser = useSelector(selectAppUser);
  const firebaseUser = useSelector(selectUser);
  const isInitializedRef = useRef(false);
  // Get root account information
  const {
    rootAccountId,
    rootAccountName,
    rootAccountParentId,
    rootAccountParentName,
    isLoading: isRootAccountLoading,
  } = useGetRootAccountInfo();

  // Initialize Intercom when user is logged in and root account info is available
  useEffect(() => {
    if (!appUser || !firebaseUser || isRootAccountLoading) {
      return;
    }

    // Convert Firebase creationTime to Unix timestamp (in seconds)
    let createdAt: number | undefined;
    if (firebaseUser.metadata?.creationTime) {
      const creationDate = new Date(firebaseUser.metadata.creationTime);
      createdAt = Math.floor(creationDate.getTime() / 1000);
    }

    // Build the full user name
    const name = `${appUser.first_name} ${appUser.last_name}`.trim();

    // Boot Intercom with user data
    // Define the type inline to avoid any, matching IntercomSettings structure
    const settings: {
      app_id: string;
      user_id?: string | number;
      name?: string;
      email?: string;
      platform_version?: string;
      hide_default_launcher?: boolean;
      user_hash?: string;
      created_at?: number;
      company_id?: string;
      company_name?: string;
      parent_account_id?: string;
      parent_account_name?: string;
      [key: string]: unknown;
    } = {
      app_id: APP_ID,
      user_id: appUser.user_id.toString(),
      name,
      email: appUser.email,
      platform_version: PLATFORM_VERSION,
      hide_default_launcher: false,
    };

    // Add user_hash for identity verification (required when Messenger Security is enabled)
    if (appUser.user_hash) {
      settings.user_hash = appUser.user_hash;
    }

    // Add created_at if available
    if (createdAt) {
      settings.created_at = createdAt;
    }

    // Add root account information if available
    if (rootAccountId) {
      settings.company_id = rootAccountId;
    }
    if (rootAccountName) {
      settings.company_name = rootAccountName;
    }
    if (rootAccountParentId) {
      settings.parent_account_id = rootAccountParentId;
    }
    if (rootAccountParentName) {
      settings.parent_account_name = rootAccountParentName;
    }

    try {
      // Initialize Intercom - this adds window.Intercom
      Intercom(settings);
      isInitializedRef.current = true;
    } catch (error) {
      console.error('Failed to initialize Intercom:', error);
    }

    // Cleanup function
    return () => {
      if (isInitializedRef.current && typeof window !== 'undefined' && window.Intercom) {
        try {
          window.Intercom('shutdown');
          isInitializedRef.current = false;
        } catch (error) {
          console.error('Error shutting down Intercom:', error);
        }
      }
    };
  }, [
    appUser,
    firebaseUser,
    rootAccountId,
    rootAccountName,
    rootAccountParentId,
    rootAccountParentName,
    isRootAccountLoading,
  ]);

  // Method to open Intercom messenger
  const openIntercom = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom && isInitializedRef.current) {
      try {
        window.Intercom('show');
      } catch (error) {
        console.error('Error opening Intercom:', error);
      }
    } else {
      console.warn('Intercom is not initialized yet');
    }
  }, []);

  return {
    openIntercom,
  };
};
